import { parseApiErrorMessage } from "../../../common/apiErrors"
import { CALL_DISPOSITIONS } from "./callDispositions"

export const POWER_DIALER_SOURCES = {
  mls: "mls",
  uploaded: "uploaded",
  candidates: "candidates",
}

export const POWER_DIALER_SOURCE_LABELS = {
  mls: "MLS Agents",
  uploaded: "Import Leads",
  candidates: "Candidates",
}

export function sessionSourceLabel(source) {
  return POWER_DIALER_SOURCE_LABELS[source] || source || "Power Dialer"
}

export function itemOfficeName(item) {
  return String(item?.office_name || item?.company_name || "").trim()
}

export function shouldShowActiveDialerOfficeName({
  isBusy,
  wrappingUp,
  officeName,
} = {}) {
  return Boolean(officeName && isBusy && !wrappingUp)
}

export function contactFromDialerItem(item, session = null) {
  if (!item) return null
  const officeName = itemOfficeName(item)
  const isCandidate = item.lead_source === POWER_DIALER_SOURCES.candidates
  const sessionFields = session
    ? { dialerSessionId: session.id, amd: session.amd || null }
    : {}
  if (isCandidate) {
    return {
      id: item.candidate_id || item.lead_ref_id,
      candidate_name: item.name || "",
      company_name: officeName,
      phone: item.phone || "",
      isLead: false,
      leadSource: item.lead_source,
      leadRefId: item.lead_ref_id,
      candidate_id: item.candidate_id || item.lead_ref_id,
      ...sessionFields,
    }
  }
  return {
    candidate_name: item.name || "",
    company_name: officeName,
    phone: item.phone || "",
    isLead: true,
    leadSource: item.lead_source,
    leadRefId: item.lead_ref_id,
    candidate_id: item.candidate_id,
    ...sessionFields,
  }
}

/** Re-send a finished session's AMD choices when retrying its no-answers. */
export function amdPayloadFromSession(session) {
  const amd = session?.amd
  if (!amd?.enabled) return { enabled: false }
  return {
    enabled: true,
    machine_action: amd.machine_action || "text",
    mode: amd.mode || "listen_in",
    sms_campaign_id: amd.sms_campaign?.id ?? null,
    voicemail_recording_id: amd.voicemail_recording?.id ?? null,
  }
}

export function sessionProgressLabel(session) {
  if (!session) return ""
  const position = Math.min(session.current_index + 1, session.queue_count || 0)
  return `${position} of ${session.queue_count}`
}

export function sessionProgressPercent(session) {
  const total = Number(session?.queue_count) || 0
  if (!total) return 0
  const done = Math.min(Number(session.current_index) || 0, total)
  return Math.round((done / total) * 100)
}

export function upcomingLeads(session, limit = 2) {
  if (!Array.isArray(session?.upcoming_items)) return []
  return session.upcoming_items.slice(0, limit)
}

export function leadInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return "?"
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("")
}

export function dialerSessionErrorMessage(error, fallback) {
  return parseApiErrorMessage(
    error?.response?.data,
    fallback || "Could not start Power Dialer."
  )
}

export function sessionOutcomeRows(session) {
  const counts = session?.outcome_counts || {}
  return CALL_DISPOSITIONS.map((option) => ({
    value: option.value,
    label: option.label,
    count: Number(counts[option.value]) || 0,
  })).filter((row) => row.count > 0)
}

export function sessionSummaryCounts(session) {
  const logged = Number(session?.completed_count) || 0
  const skipped = Number(session?.skipped_count) || 0
  return {
    logged,
    skipped,
    booked: Number(session?.outcome_counts?.booked) || 0,
    attempted: logged + skipped,
  }
}

export function retryableDialerLeads(session, { includeBusy = false } = {}) {
  const wanted = new Set(["no_answer"])
  if (includeBusy) wanted.add("busy")
  return (
    Array.isArray(session?.queue_items) ? session.queue_items : []
  ).filter(
    (item) =>
      item?.status === "completed" &&
      wanted.has(item.disposition) &&
      item.lead_ref_id
  )
}

function countPhrase(count, singular, plural) {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`
}

/**
 * The start dialog's "8 of 100 can be dialed" and, when some were left out,
 * "90 have no callable phone, 2 are on DNC". DNC leads are never queued, so
 * they only show up here. Null when the preview has no totals (older API).
 */
export function dialableSummary(preview) {
  if (preview?.total_count === undefined || preview?.total_count === null) {
    return null
  }
  const total = Number(preview.total_count) || 0
  const noPhone = Number(preview.no_phone_count) || 0
  const dnc = Number(preview.dnc_skipped_count) || 0
  const dialable = Math.max(0, total - noPhone - dnc)
  const reasons = []
  if (noPhone > 0) {
    reasons.push(
      countPhrase(noPhone, "has no callable phone", "have no callable phone")
    )
  }
  if (dnc > 0) {
    reasons.push(countPhrase(dnc, "is on DNC", "are on DNC"))
  }
  return {
    headline: `${dialable.toLocaleString()} of ${total.toLocaleString()} can be dialed`,
    breakdown: reasons.join(", "),
  }
}
